import { useState, useEffect, MouseEventHandler } from 'react';
import { Aside, Button, RAside, raulUtils } from 'lib/raul';
import { APP } from 'lib/strings';

interface Props {
  article: HTMLElement;
  title?: string;
}
interface LayerProps {
  finalTable: ContentGroup;
}
interface ContentItem {
  group: ContentGroup | null;
  headerNum: number;
  id: string;
  name: string;
}

type ContentGroup = ContentItem[];

const findLowestHeaderNum = (layer: ContentGroup) => {
  let lowestNum = 6;

  layer.forEach((item) => {
    if (item.headerNum < lowestNum) {
      lowestNum = item.headerNum;
    }
  });

  return lowestNum;
};

const settleLayers = (array: ContentGroup) => {
  // the lowest h tag number is considered the most senior of this layer,
  // all others header items get grouped into consecutive subgroups, to be settled into their own layers
  const seniorHeaderNum = findLowestHeaderNum(array);
  const layer: ContentGroup = [];
  let subgroup: ContentGroup = [];

  array.forEach((item) => {
    if (item.headerNum !== seniorHeaderNum) {
      // if the item's header is not senior, put item into a temporary subgroup
      subgroup.push(item);
    } else {
      // if the item's header is senior, finalize and push the temporary subgroup if it exists
      // then push the senior item, and empty the temporary subgroup
      if (subgroup.length) {
        layer.push({
          group: settleLayers(subgroup),
          headerNum: 7,
          id: 'group',
          name: 'group',
        });
      }
      layer.push(item);
      subgroup = [];
    }
  });

  // if any items remain in the temporary subgroup, finalize and push
  if (subgroup.length) {
    layer.push({
      group: settleLayers(subgroup),
      headerNum: 7,
      id: 'group',
      name: 'group',
    });
  }

  return layer;
};

const getTable = (article: HTMLElement) => {
  const table: ContentGroup = [];

  const headerElementList = Array.from(
    article.querySelectorAll(
      'h1, h2, h3, h4, h5, h6'
    ) as NodeListOf<HTMLElement>
  );

  headerElementList.forEach((element) => {
    table.push({
      group: null,
      headerNum: parseInt(element.nodeName.replace(/\D/g, ''), 10),
      id: `#${element.id}`,
      name: element.innerText,
    });
  });

  return settleLayers(table);
};

const RenderLayer = ({ finalTable }: LayerProps) => {
  const scrollIntoView: MouseEventHandler<HTMLAnchorElement> = (e) => {
    e.preventDefault();

    const link = e.target as HTMLAnchorElement;
    const targeted = link?.getAttribute('data-targeted');

    if (targeted) {
      const scrollTarget = document.querySelector(targeted);

      const element = document.querySelector(`#contents-aside`) as RAside;

      element?.close();

      setTimeout(() => {
        scrollTarget?.scrollIntoView(true);
        // 70px ensures the title clicked is not hidden behind the navbar
        window.scrollBy(0, -70);
      }, 50);
    }
  };

  return (
    <>
      {finalTable.map((item, index) => (
        <ul key={`${String(index)}-${item.name}`} className="r-pl-5">
          {item.group ? (
            <RenderLayer finalTable={item.group} />
          ) : (
            <li>
              <a
                className="r-text-primary"
                data-targeted={item.id}
                href={item.id}
                onClick={scrollIntoView}
              >
                {item.name}
              </a>
            </li>
          )}
        </ul>
      ))}
    </>
  );
};

const TableOfContents = ({ article, title }: Props) => {
  const [table, setTable] = useState<ContentGroup>([]);

  useEffect(() => {
    const temp = getTable(article);

    setTable(temp);
  }, [article]);

  const scrollToTop: MouseEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();

    const element = document.querySelector(`#contents-aside`) as RAside;

    element?.close();

    setTimeout(() => {
      window.scrollTo({ top: 0 });
    }, 50);
  };

  return (
    <Aside
      actions={
        <>
          <Button handleClick={raulUtils.handleCloseAside('contents')}>
            {APP.CLOSE}
          </Button>
          <Button
            className="r-ml-3"
            handleClick={scrollToTop}
            variant="primary"
          >
            {APP.SCROLL_TOP}
          </Button>
        </>
      }
      id="contents"
      size="small"
      subtitle={APP.TABLE_OF_CONTENTS}
      title={title}
    >
      <div className="toc">
        <RenderLayer finalTable={table} />
      </div>
    </Aside>
  );
};

export default TableOfContents;
